import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Package, Grid3X3, Edit, AlertCircle, RotateCcw, Info, CheckCircle, Shield } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
interface DeviceAppsCategoriesTableProps {
  deviceId: string;
}
export const DeviceAppsCategoriesTable = ({
  deviceId
}: DeviceAppsCategoriesTableProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const {
    toast
  } = useToast();

  // Get device details using the correct API endpoint
  const deviceData = useQuery(api.devices.get.details, deviceId ? {
    device: deviceId as any
  } : "skip");

  // Get all available categories
  const allCategories = useQuery(api.appCategories.list.categories, {});

  // Get device app configuration - this contains all necessary information
  const deviceAppConfig = useQuery(api.devices.apps.getConfig, deviceData?.device?.deviceConfig ? {
    deviceConfig: deviceData.device.deviceConfig
  } : "skip");

  // Mutation for updating device apps
  const updateDeviceApps = useMutation(api.devices.apps.update);
  const isLoading = deviceData === undefined || allCategories === undefined || deviceData?.device?.deviceConfig && deviceAppConfig === undefined;

  // Debug logging for API response structure  
  if (process.env.NODE_ENV === 'development' && deviceAppConfig) {
    console.log('Device App Config API Response:', {
      deviceAppConfig,
      appFilter: deviceAppConfig.appFilter,
      appFilterKeys: Object.keys(deviceAppConfig.appFilter || {}),
      sampleCategories: Object.entries(deviceAppConfig.appFilter || {}).slice(0, 3)
    });
  }
  const getCategoryStatus = (categoryName: string) => {
    if (!deviceAppConfig?.appFilter?.[categoryName]) {
      // No configuration means this category is blocked by default
      // Only categories explicitly configured are allowed
      return 'Blocked';
    }
    const filterConfig = deviceAppConfig.appFilter[categoryName];
    const isDeviceOverride = filterConfig.source === 'device';
    if (isDeviceOverride) {
      // Device override exists
      if (filterConfig.tagFilter === true) {
        return 'Allowed';
      } else if (filterConfig.tagFilter === false) {
        return 'Blocked';
      }
    } else {
      // Level-based permissions
      if (filterConfig.tagFilter === true) {
        return 'Allowed';
      } else if (filterConfig.tagFilter === false) {
        return 'Blocked';
      } else if (typeof filterConfig.tagFilter === 'object' && filterConfig.tagFilter !== null) {
        return 'Allowed with rules';
      }
    }

    // Default to blocked if configuration exists but doesn't match expected patterns
    return 'Blocked';
  };
  const getCategoryRuleDescription = (categoryName: string) => {
    const filterConfig = deviceAppConfig?.appFilter?.[categoryName];
    if (!filterConfig || typeof filterConfig.tagFilter !== 'object' || filterConfig.tagFilter === null) {
      return null;
    }
    return (filterConfig as any).aiDescription || "Custom rules applied to this category";
  };
  const getCategorySource = (categoryName: string) => {
    if (!deviceAppConfig?.appFilter?.[categoryName]) {
      // Category not in appFilter - check if device has a level assigned
      return deviceData?.level ? 'level' : 'system default';
    }
    
    const filterConfig = deviceAppConfig.appFilter[categoryName];
    
    // Debug logging to understand the API response structure
    if (process.env.NODE_ENV === 'development') {
      console.log(`Category ${categoryName} config:`, {
        filterConfig,
        source: filterConfig?.source,
        hasSource: 'source' in filterConfig,
        keys: Object.keys(filterConfig || {})
      });
    }
    
    // Validate that filterConfig has the expected structure
    if (!filterConfig || typeof filterConfig !== 'object') {
      console.warn(`Invalid filter config for category ${categoryName}:`, filterConfig);
      return 'level';
    }
    
    // If source is explicitly marked as device, it's a device override
    if (filterConfig.source === 'device') {
      return 'device';
    }
    
    // If source is explicitly marked as level, it's from level configuration
    if (filterConfig.source === 'level') {
      return 'level';
    }
    
    // If no source field or unknown source, log it and default to level
    if (!filterConfig.source) {
      console.warn(`No source field found for category ${categoryName}, defaulting to level`);
    } else {
      console.warn(`Unknown source value for category ${categoryName}: ${filterConfig.source}`);
    }
    
    // All other cases are level-based settings
    return 'level';
  };
  const getLevelStatus = (categoryName: string) => {
    // Find what the level setting would be for this category
    const filterConfig = deviceAppConfig?.appFilter?.[categoryName];
    if (!filterConfig) {
      // No configuration in the appFilter means this category is BLOCKED at the level
      // Only categories explicitly configured in the level's appFilter are allowed
      return 'Blocked';
    }
    if (filterConfig.source === 'level') {
      // It's a level-based setting - use the actual level configuration
      if (filterConfig.tagFilter === true) {
        return 'Allowed';
      } else if (filterConfig.tagFilter === false) {
        return 'Blocked';
      } else if (typeof filterConfig.tagFilter === 'object' && filterConfig.tagFilter !== null) {
        return 'Allowed with rules';
      }
    } else if (filterConfig.source === 'device') {
      // It's a device override - we need to determine what the underlying level setting is
      // Since the device config merges level and device data, we need to infer the level status

      // If there's a device override, we can check if the device has a level assigned
      if (deviceData?.level) {
        // If the device has a level but this category has a device override,
        // the level likely had this category as blocked (otherwise why override?)
        // However, we can't be 100% certain without querying the level directly

        // For categories not in the level's appFilter, they default to blocked
        // Since this has a device override, it suggests the level setting was different
        // We'll make an educated guess: if device is overriding to allow, level was probably blocked
        // If device is overriding to block, level was probably allowed

        if (filterConfig.tagFilter === true) {
          // Device is allowing it, so level was probably blocking it
          return 'Blocked';
        } else if (filterConfig.tagFilter === false) {
          // Device is blocking it, so level was probably allowing it
          return 'Allowed';
        } else if (typeof filterConfig.tagFilter === 'object' && filterConfig.tagFilter !== null) {
          // Device has custom rules, level probably had different settings
          return 'Blocked'; // Conservative assumption
        }
      }

      // If no level assigned, default to blocked
      return 'Blocked';
    }

    // Default case - if category is not configured at level, it's blocked
    return 'Blocked';
  };
  const getStatusBadgeColor = (status: string) => {
    if (status === 'Allowed' || status === 'Allow') {
      return 'bg-green-100 text-green-800';
    } else if (status === 'Blocked' || status === 'Block') {
      return 'bg-red-100 text-red-800';
    } else if (status === 'Allowed with rules') {
      return 'bg-yellow-100 text-yellow-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const getRowBackgroundColor = (status: string) => {
    if (status === 'Allowed' || status === 'Allow' || status === 'Allowed with rules') {
      return 'bg-green-50 hover:bg-green-100';
    } else if (status === 'Blocked' || status === 'Block') {
      return 'bg-red-50 hover:bg-red-100';
    }
    return 'hover:bg-gray-50';
  };
  const handleStatusChangeLocal = (categoryName: string, newStatus: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [categoryName]: newStatus
    }));
  };
  const handleSaveChanges = async () => {
    try {
      // Get current device-only app filter configuration (don't include level-based ones)
      const currentAppFilter: Record<string, boolean | {
        exclude?: {
          tags: string[];
          type: "any" | "all";
        };
        include?: {
          tags: string[];
          type: "any" | "all";
        };
      }> = {};

      // Only include existing device overrides, not level-based configurations
      Object.entries(deviceAppConfig?.appFilter || {}).forEach(([catName, config]) => {
        if (config && typeof config === 'object' && 'tagFilter' in config && config.source === 'device') {
          currentAppFilter[catName] = config.tagFilter;
        }
      });

      // Apply all pending changes
      Object.entries(pendingChanges).forEach(([categoryName, newStatus]) => {
        const levelStatus = getLevelStatus(categoryName);
        if (newStatus === 'allowed') {
          currentAppFilter[categoryName] = true;
        } else if (newStatus === 'blocked') {
          currentAppFilter[categoryName] = false;
        } else if (newStatus === 'allowed-with-rules') {
          // This would need to be implemented to allow users to set custom rules
          // For now, we'll treat it as allowed
          currentAppFilter[categoryName] = true;
        }

        // If the selected status matches the level status, remove device override (revert to level)
        const isRevertingToLevel = newStatus === 'allowed' && levelStatus === 'Allowed' || newStatus === 'blocked' && levelStatus === 'Blocked' || newStatus === 'allowed-with-rules' && levelStatus === 'Allowed with rules';
        if (isRevertingToLevel) {
          delete currentAppFilter[categoryName];
        }
      });

      // Include the entire configuration
      await updateDeviceApps({
        device: deviceId as any,
        appFilter: currentAppFilter,
        allowedApps: Object.keys(deviceAppConfig?.allowedApps || {}).filter(appId => deviceAppConfig?.allowedApps[appId] === 'device') as any[],
        blockedApps: Object.keys(deviceAppConfig?.blockedApps || {}).filter(appId => deviceAppConfig?.blockedApps[appId] === 'device') as any[]
      });
      toast({
        title: "Changes Saved",
        description: `${Object.keys(pendingChanges).length} category changes have been saved.`
      });
      setPendingChanges({});
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving changes:', error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleCancelChanges = () => {
    setPendingChanges({});
    setIsEditMode(false);
  };
  const handleRevertToDefaults = async () => {
    try {
      // Remove all device-specific category overrides by sending empty appFilter
      // The device should have no category overrides when reverting to level defaults
      await updateDeviceApps({
        device: deviceId as any,
        appFilter: {},
        // Empty - no device-specific category overrides
        allowedApps: Object.keys(deviceAppConfig?.allowedApps || {}).filter(appId => deviceAppConfig?.allowedApps[appId] === 'device') as any[],
        blockedApps: Object.keys(deviceAppConfig?.blockedApps || {}).filter(appId => deviceAppConfig?.blockedApps[appId] === 'device') as any[]
      });
      setIsEditMode(false);
      toast({
        title: "Reverted to Defaults",
        description: "All category overrides have been removed. Using level defaults."
      });
    } catch (error) {
      console.error('Error reverting to defaults:', error);
      toast({
        title: "Error",
        description: "Failed to revert to defaults. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAllowAll = async () => {
    try {
      // Create appFilter with all categories set to true (allowed)
      const allAllowedFilter: Record<string, boolean> = {};
      allCategories?.forEach((category: any) => {
        allAllowedFilter[category.name] = true;
      });

      await updateDeviceApps({
        device: deviceId as any,
        appFilter: allAllowedFilter,
        allowedApps: Object.keys(deviceAppConfig?.allowedApps || {}).filter(appId => deviceAppConfig?.allowedApps[appId] === 'device') as any[],
        blockedApps: Object.keys(deviceAppConfig?.blockedApps || {}).filter(appId => deviceAppConfig?.blockedApps[appId] === 'device') as any[]
      });

      toast({
        title: "All Categories Allowed",
        description: "All app categories have been set to allowed."
      });
    } catch (error) {
      console.error('Error allowing all categories:', error);
      toast({
        title: "Error",
        description: "Failed to allow all categories. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleBlockAll = async () => {
    try {
      // Create appFilter with all categories set to false (blocked)
      const allBlockedFilter: Record<string, boolean> = {};
      allCategories?.forEach((category: any) => {
        allBlockedFilter[category.name] = false;
      });

      await updateDeviceApps({
        device: deviceId as any,
        appFilter: allBlockedFilter,
        allowedApps: Object.keys(deviceAppConfig?.allowedApps || {}).filter(appId => deviceAppConfig?.allowedApps[appId] === 'device') as any[],
        blockedApps: Object.keys(deviceAppConfig?.blockedApps || {}).filter(appId => deviceAppConfig?.blockedApps[appId] === 'device') as any[]
      });

      toast({
        title: "All Categories Blocked",
        description: "All app categories have been set to blocked."
      });
    } catch (error) {
      console.error('Error blocking all categories:', error);
      toast({
        title: "Error",
        description: "Failed to block all categories. Please try again.",
        variant: "destructive"
      });
    }
  };
  if (isLoading) {
    return <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span>Loading app categories...</span>
        </CardContent>
      </Card>;
  }
  if (!deviceData?.device?.deviceConfig) {
    return <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-amber-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Device Configuration</h3>
          <p className="text-gray-600 text-center">
            This device has no configuration. Please configure the device to see app categories.
          </p>
        </CardContent>
      </Card>;
  }
  if (!allCategories || allCategories.length === 0) {
    return <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Grid3X3 className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Categories Available</h3>
          <p className="text-gray-600 text-center">
            No app categories are currently defined in the system.
          </p>
        </CardContent>
      </Card>;
  }
  return <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">App Categories</h3>
          
          
        </div>
        <div className="flex gap-2">
          {isEditMode ? <>
              <Button variant="outline" size="sm" onClick={handleCancelChanges}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges} disabled={Object.keys(pendingChanges).length === 0}>
                Save Changes
              </Button>
            </> : <>
              <Button variant="outline" size="sm" onClick={handleAllowAll}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Allow All
              </Button>
              <Button variant="outline" size="sm" onClick={handleBlockAll}>
                <Shield className="w-4 h-4 mr-1" />
                Block All
              </Button>
              {deviceData?.level && (
                <Button variant="outline" size="sm" onClick={handleRevertToDefaults}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Revert to level defaults
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
            </>}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-0">Category</TableHead>
                <TableHead className="w-20 sm:w-32">Status</TableHead>
                <TableHead className="hidden sm:table-cell w-20">Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allCategories.map((category: any) => {
                const enrichedCategory = {
                  ...category,
                  status: getCategoryStatus(category.name),
                  source: getCategorySource(category.name)
                };
                return enrichedCategory;
              }).sort((a, b) => {
                // First sort by status priority (allowed first, then blocked)
                const aIsAllowed = a.status === 'Allowed' || a.status === 'Allow' || a.status === 'Allowed with rules';
                const bIsAllowed = b.status === 'Allowed' || b.status === 'Allow' || b.status === 'Allowed with rules';
                if (aIsAllowed && !bIsAllowed) return -1;
                if (!aIsAllowed && bIsAllowed) return 1;

                // Then sort alphabetically within each group
                return a.name.localeCompare(b.name);
              }).map((category: any) => (
                <TableRow key={category._id || category.name} className={getRowBackgroundColor(category.status)}>
                    <TableCell className="min-w-0 py-2">
                      <div className="font-medium text-sm truncate">{category.name}</div>
                      <div className="text-xs text-gray-500 sm:hidden">
                        {category.source} • {category.status}
                      </div>
                    </TableCell>
                    <TableCell className="w-20 sm:w-32 py-2">
                       {isEditMode ? <Select value={pendingChanges[category.name] || (category.status === 'Allowed' ? 'allowed' : category.status === 'Blocked' ? 'blocked' : 'allowed-with-rules')} onValueChange={value => handleStatusChangeLocal(category.name, value)}>
                          <SelectTrigger className="w-full text-xs sm:text-sm h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="allowed">
                              <div className="flex items-center justify-between w-full">
                                <span>Allow</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="blocked">
                              <div className="flex items-center justify-between w-full">
                                <span>Block</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="allowed-with-rules">
                              <div className="flex items-center justify-between w-full">
                                <span>Allow with rules</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select> : <div className="flex items-center gap-2 h-8">
                          <Badge className={getStatusBadgeColor(category.status)} variant="secondary">
                            {category.status}
                          </Badge>
                          {getCategoryRuleDescription(category.name) && <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Info className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{getCategoryRuleDescription(category.name)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>}
                        </div>}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell w-20 py-2">
                      <span className="text-sm capitalize">{category.source}</span>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isEditMode && <div className="text-xs text-gray-500 p-4 bg-blue-50 rounded-lg">
          <p><strong>Edit Mode:</strong> Use the dropdowns to change category permissions for this device. 
          Click "Save Changes" to apply your changes or "Cancel" to discard them.</p>
        </div>}
    </div>;
};